import { Link } from 'react-router-dom';
import type { Lot, Asset } from '@cherry/shared';
import { shortenAddress } from '@cherry/shared';
import { sortAssetsForDisplay } from './AssetBadge';
import { AssetShowcaseHero, AssetShowcaseThumb } from './AssetShowcase';
import { StatusBadge } from './StatusBadge';
import { ReputationBadge } from './ReputationBadge';

function formatBuyNow(nanotons: string): string {
  const n = Number(nanotons);
  if (Number.isNaN(n) || n === 0) return '0';
  return (n / 1_000_000_000).toFixed(2);
}

function timeLeft(expiresAt: Date | null): string | null {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt).getTime() - Date.now();
  if (diff <= 0) return 'Expired';
  const hours = Math.floor(diff / 3_600_000);
  if (hours >= 24) return `${Math.floor(hours / 24)}d left`;
  if (hours >= 1) return `${hours}h left`;
  return `${Math.floor(diff / 60_000)}m left`;
}

function DepositProgress({ assets }: { assets: Lot['assets'] }) {
  const nonTonAssets = assets.filter((a) => a.kind !== 'TON');
  if (nonTonAssets.length === 0) return null;

  const deposited = nonTonAssets.filter((a) => a.deposited).length;
  const total = nonTonAssets.length;

  return (
    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-warning-bg border border-warning-border">
      <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-warning shrink-0" />
      <div className="flex-1 min-w-0">
        <span className="text-xs text-warning-fg font-medium">
          {deposited} of {total} asset{total > 1 ? 's' : ''} deposited
        </span>
        {deposited < total && (
          <div className="w-full bg-warning/20 rounded-full h-1 mt-1">
            <div
              className="bg-warning h-1 rounded-full transition-all"
              style={{ width: `${total > 0 ? (deposited / total) * 100 : 0}%` }}
            />
          </div>
        )}
      </div>
      <span className="text-xs font-bold text-cherry-600 shrink-0">Deposit →</span>
    </div>
  );
}

function pickHeroAsset(assets: Asset[]): Asset | null {
  const sorted = sortAssetsForDisplay(assets);
  return sorted[0] ?? null;
}

function getSecondaryAssets(assets: Asset[]): Asset[] {
  const sorted = sortAssetsForDisplay(assets);
  return sorted.slice(1).filter((a) => {
    if (a.kind === 'TON' && (!a.amountNano || a.amountNano === '0')) return false;
    return true;
  });
}

const MAX_THUMBS = 4;

export function LotCard({ lot }: { lot: Lot }) {
  const hasBuyNow = lot.buyNowTon && lot.buyNowTon !== '0';
  const expiry = timeLeft(lot.expiresAt);
  const isHot = lot.offerCount >= 3;
  const isPendingDeposit = lot.status === 'PENDING_DEPOSITS';

  const heroAsset = pickHeroAsset(lot.assets);
  const secondaryAssets = getSecondaryAssets(lot.assets);
  const visibleThumbs = secondaryAssets.slice(0, MAX_THUMBS);
  const extraCount = secondaryAssets.length - MAX_THUMBS;

  const borderCls = isPendingDeposit
    ? 'border-warning-border hover:border-cherry-300'
    : isHot
      ? 'border-cherry-300 hover:border-cherry-400'
      : 'border-border hover:border-cherry-300';

  return (
    <Link
      to={`/lot/${lot.lotId}`}
      aria-label={`Deal ${lot.lotId}: ${lot.wishlistText || 'No description'}`}
      className={`group block rounded-xl border overflow-hidden transition-all no-underline text-inherit active:scale-[0.98] shadow-[var(--shadow-card)] hover:shadow-[var(--shadow-card-hover)] bg-surface-raised ${borderCls}`}
    >
      {/* Hero image area */}
      <div className="relative">
        {heroAsset ? (
          <AssetShowcaseHero asset={heroAsset} />
        ) : (
          <div className="w-full aspect-[4/3] bg-gradient-to-br from-cherry-50 to-cherry-100 dark:from-cherry-900/20 dark:to-cherry-800/10 flex items-center justify-center">
            <span className="text-4xl opacity-40">🍒</span>
          </div>
        )}

        {/* Status badge overlay */}
        <div className="absolute top-2 right-2">
          <StatusBadge status={lot.status} />
        </div>

        {/* Hot badge overlay */}
        {isHot && (
          <div className="absolute top-2 left-2">
            <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-cherry-500 text-white uppercase tracking-wider shadow-sm">
              Hot
            </span>
          </div>
        )}
      </div>

      {/* Asset strip for secondary assets */}
      {visibleThumbs.length > 0 && (
        <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border overflow-x-auto scrollbar-hide">
          {visibleThumbs.map((asset, i) => (
            <AssetShowcaseThumb key={i} asset={asset} />
          ))}
          {extraCount > 0 && (
            <span className="text-xs text-text-muted font-medium whitespace-nowrap">+{extraCount} more</span>
          )}
        </div>
      )}

      {/* Deal info section */}
      <div className="px-3 py-3 space-y-2">
        {/* Wishlist */}
        {lot.wishlistText && (
          <p className="text-sm text-text-secondary line-clamp-2 leading-snug">
            <span className="text-text-muted font-medium">Looking for:</span> {lot.wishlistText}
          </p>
        )}

        {/* Buy now + offers + expiry */}
        <div className="flex items-center gap-2 flex-wrap">
          {hasBuyNow && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-bold rounded-full bg-emerald-600 text-white dark:bg-emerald-500">
              ⚡ {formatBuyNow(lot.buyNowTon!)} TON
            </span>
          )}

          {lot.offerCount > 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-surface-alt text-text-secondary">
              {lot.offerCount} offer{lot.offerCount !== 1 ? 's' : ''}
            </span>
          )}

          {expiry && (
            <span className={`ml-auto text-xs font-medium ${
              expiry === 'Expired' ? 'text-error' : 'text-text-muted'
            }`}>
              {expiry}
            </span>
          )}
        </div>

        {/* Seller line */}
        <div className="flex items-center gap-2 pt-1 border-t border-border/50">
          <span className="text-xs text-text-muted">#{lot.lotId}</span>
          <span className="text-xs text-text-muted truncate">{shortenAddress(lot.sellerAddress)}</span>
          <ReputationBadge address={lot.sellerAddress} />
        </div>

        {/* Deposit progress for PENDING_DEPOSITS lots */}
        {isPendingDeposit && <DepositProgress assets={lot.assets} />}
      </div>
    </Link>
  );
}
